import instance, { ApiResponse } from 'api';
import { AxiosError, AxiosResponse } from 'axios';
import { BaseApi } from 'baseApi';
import { endpoints } from 'endpoints.config';
import { Package } from 'pages/newSubscription/models';
import { ISchoolDetails } from 'pages/subscribe/models';
import { PackagePromo } from 'subscriptionOptions/types';
import {
  RenewSubscriptionFormValues,
  SubscriptionsParser,
} from 'pages/subscriptions/models';
import { UpgradeSubscriptionParser } from 'pages/upgradeSubscription/models';

const UNKOWN_ERROR_MESSAGE = 'Sorry, something went wrong. Please try again.';

export class SubscriptionsApi extends BaseApi {
  // get subscription list
  public static getSubscriptionsList = async (): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.subscriptions.list
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const list = result.data.details.listPackages;
        const parsedList = list
          ? SubscriptionsParser.parseGetSubscriptionList(list)
          : null;

        const data = { ...result.data.details, listPackages: parsedList };

        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors?.length) {
          return SubscriptionsApi.errorResponse(
            SubscriptionsParser.parseError(errors)
          );
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      /* The endpoint answers 400 for a promo code the pricing proc rejects, so the body carries
      the real reason; axios routes it here rather than the branch above. */
      if (error.response) {
        const errors = error.response.data?.errors;
        if (errors?.length) {
          return SubscriptionsApi.errorResponse(
            SubscriptionsParser.parseError(errors)
          );
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static getSchoolInfo = async (): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.subscriptions.getSchoolInfo
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static updateSchoolInfo = async (
    values: ISchoolDetails
  ): Promise<any> => {
    try {
      const payload =
        UpgradeSubscriptionParser.buildSchoolDetailsPayload(values);

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.subscriptions.updateSchoolInfo,
        payload
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static getSchoolUsers = async (): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.subscriptions.getSchoolUsers
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data.details.schoolUsers;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static updateSchoolUserAccess = async (
    selectedUsers: string[],
    selectedPackages: Package[]
  ): Promise<any> => {
    try {
      const payload = UpgradeSubscriptionParser.buildSchoolUserAccess(
        selectedUsers,
        selectedPackages
      );

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.subscriptions.updateSchoolUserAccess,
        payload
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static renewByInvoice = async (
    values: RenewSubscriptionFormValues,
    packagePromos: PackagePromo[],
    expectedGrossAmount: number
  ): Promise<any> => {
    try {
      const payload = SubscriptionsParser.buildRenewByInvoicePayload(
        values,
        packagePromos,
        expectedGrossAmount
      );

      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.subscriptions.renewByInvoice,
        payload
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data.details;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  public static autoRenewSubscription = async (
    SchoolAccountPackagesId: number,
    bAutoRenew: boolean,
  ): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.subscriptions.autoRenewal,
        {
          SchoolAccountPackagesId,
          bAutoRenew,
        }
      );
      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };   

  public static setRenewalPackages = async (
    setRenewalPackages: any,
  ): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.subscriptions.setrenewalpackages,
        setRenewalPackages
      );
      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };
  
  public static getRenewalPromoCode = async (): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.get(
        endpoints.subscriptions.getRenewalPromoCode
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        const data = result.data;
        return SubscriptionsApi.successResponse(data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  /** The packages a code is valid for. A refused code errors, so nothing is inferred from a price. */
  public static checkPromoCode = async (promoCode: string): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<{ packagesIds: number[] }>> = await instance.get(
        endpoints.subscriptions.checkPromoCode +
          '?PromoCode=' +
          encodeURIComponent(promoCode)
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        // details wraps the ids.
        const details = result.data.details;
        return SubscriptionsApi.successResponse((details && details.packagesIds) || []);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data && error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return SubscriptionsApi.errorResponse(message);
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };

  /** Applies or removes one code: exactly one field, never both. Answers with the resulting list. */
  public static setRenewalPromoCode = async (
    promoCode: string,
    action: 'set' | 'unset' = 'set'
  ): Promise<any> => {
    try {
      const result: AxiosResponse<ApiResponse<any>> = await instance.post(
        endpoints.subscriptions.setRenewalPromoCode,
        action === 'set' ? { setPromoCode: promoCode } : { unsetPromoCode: promoCode }
      );

      if (SubscriptionsApi.isResponseSuccessful(result.data)) {
        return SubscriptionsApi.successResponse(result.data);
      } else {
        const errors = result.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          // The body still reports the resulting codes, which the caller renders from.
          return { ...SubscriptionsApi.errorResponse(message), data: result.data };
        } else {
          throw new Error(); // Caught below to trigger unknown error
        }
      }
    } catch (error: AxiosError | any) {
      if (error.response) {
        const errors = error.response.data.errors;
        if (errors) {
          const message = SubscriptionsParser.parseError(errors);
          return { ...SubscriptionsApi.errorResponse(message), data: error.response.data };
        }
      }
      return SubscriptionsApi.errorResponse(UNKOWN_ERROR_MESSAGE);
    }
  };
}
